// Dashboard de acceso directo para solucionar problemas de routing
import { Dashboard } from './Dashboard';
import { useEffect, useState } from 'react';
import { Box, Center, Spinner, Text, Button, VStack, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../config/index';

// Esta es una versión simplificada que verifica la autenticación directamente
// sin depender de ProtectedRoute para romper el ciclo de redirecciones
export default function DirectDashboard() {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    // Verificar autenticación usando el servicio mejorado
    const checkAuth = async () => {
      try {
        console.log("DirectDashboard: Verificando autenticación con servicio mejorado");
        
        // Primero verificar localStorage para respuesta rápida
        const storedUser = localStorage.getItem('currentUser');
        const authToken = localStorage.getItem('authToken');
        
        if (storedUser && authToken) {
          try {
            // Verificar si el token no está completamente expirado
            const tokenData = JSON.parse(atob(authToken));
            const currentTime = Date.now();
            const gracePeriod = 30 * 60 * 1000; // 30 minutos de gracia
            
            if (tokenData.exp > (currentTime - gracePeriod)) {
              console.log("DirectDashboard: Token válido en localStorage, mostrando dashboard");
              setIsAuthenticated(true);
              setIsLoading(false);
              return;
            }
          } catch {
            console.log("DirectDashboard: Error al verificar token, continuando con verificación completa");
          }
        }
        
        // Verificación completa con el servicio de autenticación
        const user = await authService.getCurrentUser();
        
        if (user) {
          console.log("DirectDashboard: Usuario autenticado:", user.email);
          setIsAuthenticated(true);
        } else {
          console.log("DirectDashboard: No hay usuario autenticado");
          setIsAuthenticated(false);
          
          // Solo mostrar toast y redirigir después de varios intentos fallidos
          if (retryCount >= 3) {
            toast({
              title: 'Sesión expirada',
              description: 'Por favor, inicia sesión nuevamente',
              status: 'warning',
              duration: 3000,
              isClosable: true,
            });
            
            setTimeout(() => {
              navigate('/login');
            }, 1500);
          }
        }
      } catch (error) {
        console.error("DirectDashboard: Error verificando autenticación:", error);
        
        // En caso de error, intentar usar datos de localStorage como respaldo
        const storedUser = localStorage.getItem('currentUser');
        const authToken = localStorage.getItem('authToken');
        
        if (storedUser && authToken && retryCount < 5) {
          console.log("DirectDashboard: Usando datos de respaldo de localStorage");
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    // Comprobar después de un breve retraso para evitar condiciones de carrera
    const timeoutId = setTimeout(checkAuth, 200);
    
    return () => clearTimeout(timeoutId);
  }, [navigate, toast, retryCount]);

  // Reintentar verificación si falla
  useEffect(() => {
    if (!isAuthenticated && !isLoading && retryCount < 5) {
      const retryTimeout = setTimeout(() => {
        console.log(`DirectDashboard: Reintentando verificación (${retryCount + 1}/5)`);
        setRetryCount(prev => prev + 1);
        setIsLoading(true);
      }, 1000);
      
      return () => clearTimeout(retryTimeout);
    }
  }, [isAuthenticated, isLoading, retryCount]);

  // Mostrar spinner mientras se verifica la autenticación
  if (isLoading) {
    return (
      <Box minH="100vh">
        <Center h="100vh" flexDirection="column">
          <Spinner size="xl" color="red.500" mb={4} />
          <Text color="gray.600">
            Cargando dashboard...
          </Text>
        </Center>
      </Box>
    );
  }

  // Si está autenticado, mostrar el dashboard
  if (isAuthenticated) {
    return <Dashboard />;
  }

  // Si no está autenticado pero todavía no se ha redirigido
  return (
    <Box minH="100vh">
      <Center h="100vh" flexDirection="column">
        <VStack spacing={4}>
          <Text fontSize="xl" fontWeight="bold" color="red.500">
            Necesitas iniciar sesión para acceder al dashboard
          </Text>
          <Button 
            colorScheme="red" 
            onClick={() => navigate('/login')}
          >
            Ir a la página de inicio de sesión
          </Button>
        </VStack>
      </Center>
    </Box>
  );
}
